/**
 * VoicemailPanel — slide-out drawer showing voicemails.
 * Fetches from GET /api/voice/voicemails via client/lib/voice-api.ts.
 * Audio playback, transcription display, mark-as-listened, dial-back.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Phone,
  Voicemail as VoicemailIcon,
  Play,
  Pause,
  Check,
  ChevronDown,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useMyNumbers } from '../../hooks/useMyNumbers';

interface VoicemailEntry {
  id: string;
  from: string;
  to: string;
  contact_name?: string;
  contact_id?: string;
  duration_secs: number;
  recording_url?: string;
  transcription?: string;
  listened: boolean;
  listened_at?: string;
  assigned_to?: string;
  created_at: string;
}

interface VoicemailResponse {
  voicemails: VoicemailEntry[];
  total: number;
  page: number;
  per_page: number;
}

function formatDuration(secs: number): string {
  if (!secs) return '0:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** "Jun 10, 3:42 PM" — used for the "Listened on X" indicator (#553). */
function listenedOn(ts: string): string {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function relativeTime(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function VoicemailPanel({
  open,
  onClose,
  onUnreadCountChange,
}: {
  open: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}) {
  const { fromNumber, canVoice } = useMyNumbers();
  const [voicemails, setVoicemails] = useState<VoicemailEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unlistened'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const loadVoicemails = useCallback(
    async (pageNum: number) => {
      setLoading(true);
      try {
        const { fetchVoicemails } = await import('../../../lib/voice-api');
        const data: VoicemailResponse = await fetchVoicemails({
          page: pageNum,
          limit: 20,
        });
        if (pageNum === 1) {
          setVoicemails(data.voicemails);
        } else {
          setVoicemails((prev) => [...prev, ...data.voicemails]);
        }
        setTotal(data.total);
        const unlistened = data.voicemails.filter((v) => !v.listened).length;
        onUnreadCountChange?.(pageNum === 1 ? unlistened : undefined!);
      } catch {
        // Voice service may not be available
      }
      setLoading(false);
    },
    [onUnreadCountChange],
  );

  useEffect(() => {
    if (open) {
      setPage(1);
      loadVoicemails(1);
    }
  }, [open, loadVoicemails]);

  // Count unlistened on initial load for badge
  useEffect(() => {
    const unlistened = voicemails.filter((v) => !v.listened).length;
    onUnreadCountChange?.(unlistened);
  }, [voicemails, onUnreadCountChange]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadVoicemails(nextPage);
  };

  const handleExpand = async (vm: VoicemailEntry) => {
    if (expandedId === vm.id) {
      setExpandedId(null);
      stopAudio();
      return;
    }
    setExpandedId(vm.id);
    stopAudio();

    // Mark as listened
    if (!vm.listened) {
      try {
        const { markVoicemailListened } = await import('../../../lib/voice-api');
        await markVoicemailListened(vm.id);
        setVoicemails((prev) =>
          prev.map((v) =>
            v.id === vm.id ? { ...v, listened: true, listened_at: new Date().toISOString() } : v,
          ),
        );
      } catch {
        // ignore
      }
    }
  };

  const handlePlay = async (vm: VoicemailEntry) => {
    if (playingId === vm.id) {
      // Toggle pause/play
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      }
      return;
    }

    stopAudio();
    setPlayingId(vm.id);

    try {
      const { fetchRecordingUrl } = await import('../../../lib/voice-api');
      const url = await fetchRecordingUrl(vm.id);
      setAudioUrl(url);
    } catch {
      setPlayingId(null);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setPlayingId(null);
    setAudioUrl(null);
  };

  // Auto-play when URL is set
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(() => {});
    }
  }, [audioUrl]);

  const handleDial = (vm: VoicemailEntry) => {
    if (!canVoice) return;
    const sp = (window as unknown as Record<string, unknown>)['ApexSoftphone'] as
      | {
          dial: (id: string | null, name: string | null, phone: string, fromNumber: string) => void;
        }
      | undefined;
    sp?.dial(vm.contact_id || null, vm.contact_name || null, vm.from, fromNumber);
    onClose();
  };

  const filtered = voicemails.filter((v) => {
    if (filter === 'unlistened') return !v.listened;
    return true;
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-[400px] bg-card border-l border-border/60 z-50 flex flex-col shadow-2xl"
          >
            {/* Hidden audio element */}
            <audio
              ref={audioRef}
              onEnded={() => setPlayingId(null)}
              onError={() => setPlayingId(null)}
            />

            {/* Header */}
            <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <VoicemailIcon className="w-4 h-4 text-primary stroke-[1.5]" />
                <h3 className="text-[0.9375rem] font-semibold text-foreground">Voicemail</h3>
                <span className="text-[0.6875rem] text-muted-foreground">({total})</span>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors cursor-pointer"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Filter tabs */}
            <div className="px-5 py-2 border-b border-border/60 flex gap-2">
              {(['all', 'unlistened'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded-lg text-[0.6875rem] font-semibold transition-colors cursor-pointer capitalize ${
                    filter === f
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  }`}
                >
                  {f === 'unlistened' ? 'New' : f}
                </button>
              ))}
            </div>

            {/* Voicemail list */}
            <div className="flex-1 overflow-y-auto">
              {loading && voicemails.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
                  <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin mr-2" />
                  Loading...
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <VoicemailIcon className="w-8 h-8 mb-2 opacity-30" />
                  <span className="text-[0.8125rem]">No voicemails</span>
                </div>
              ) : (
                <>
                  {filtered.map((vm) => {
                    const displayName = vm.contact_name || vm.from;
                    const isExpanded = expandedId === vm.id;
                    const isPlaying = playingId === vm.id;

                    return (
                      <div
                        key={vm.id}
                        className={`border-b border-border/40 ${!vm.listened ? 'bg-primary/[0.02]' : ''}`}
                      >
                        {/* Summary row */}
                        <button
                          onClick={() => handleExpand(vm)}
                          title={
                            vm.listened && vm.listened_at
                              ? `Listened on ${listenedOn(vm.listened_at)}`
                              : undefined
                          }
                          className="w-full px-5 py-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors cursor-pointer text-left"
                        >
                          {/* Unlistened dot */}
                          <div className="w-2 flex-shrink-0">
                            {!vm.listened && (
                              <span className="block w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span
                                className={`text-[0.8125rem] truncate ${!vm.listened ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}
                              >
                                {displayName}
                              </span>
                              <span className="text-[0.625rem] text-muted-foreground flex-shrink-0 ml-2">
                                {relativeTime(vm.created_at)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[0.6875rem] text-muted-foreground">
                                {formatDuration(vm.duration_secs)}
                              </span>
                              {vm.transcription && (
                                <span className="text-[0.6875rem] text-muted-foreground truncate max-w-[200px]">
                                  {vm.transcription.slice(0, 60)}
                                  {vm.transcription.length > 60 ? '...' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="px-5 pb-4 space-y-3">
                            {/* Audio player */}
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handlePlay(vm)}
                                className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center hover:bg-primary/90 transition-colors cursor-pointer flex-shrink-0"
                              >
                                {isPlaying ? (
                                  <Pause className="w-4 h-4 text-white" />
                                ) : (
                                  <Play className="w-4 h-4 text-white ml-0.5" />
                                )}
                              </button>
                              <div className="flex-1">
                                <div className="text-[0.75rem] font-medium text-foreground">
                                  {formatDuration(vm.duration_secs)}
                                </div>
                                <div className="text-[0.625rem] text-muted-foreground">
                                  {vm.from}
                                </div>
                              </div>
                              <button
                                onClick={() => handleDial(vm)}
                                disabled={!canVoice}
                                className="w-9 h-9 rounded-lg bg-brand-green/10 text-brand-green hover:bg-brand-green/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors cursor-pointer border border-brand-green/20"
                                title={
                                  canVoice
                                    ? 'Call back'
                                    : 'Selected number does not support voice — pick a call-capable number'
                                }
                              >
                                <Phone className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Transcription */}
                            {vm.transcription && (
                              <div className="bg-secondary/30 rounded-xl p-3">
                                <div className="text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider mb-1">
                                  Transcription
                                </div>
                                <p className="text-[0.75rem] text-foreground leading-relaxed">
                                  {vm.transcription}
                                </p>
                              </div>
                            )}

                            {/* Listened badge — surfaces listened_at (#553) */}
                            {vm.listened && (
                              <div
                                className="flex items-center gap-1 text-[0.625rem] text-muted-foreground"
                                title={
                                  vm.listened_at
                                    ? new Date(vm.listened_at).toLocaleString()
                                    : undefined
                                }
                              >
                                <Check className="w-3 h-3" />
                                {vm.listened_at
                                  ? `Listened on ${listenedOn(vm.listened_at)}`
                                  : 'Listened'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Load more */}
                  {voicemails.length < total && (
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="w-full py-3 text-[0.75rem] font-semibold text-primary hover:bg-primary/5 transition-colors cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                      {loading ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
